import React, { useEffect, useRef } from 'react';
import { Container } from 'react-bootstrap';
import { motion } from 'framer-motion';

const CFG = {
  count: 130,
  color: '200, 220, 255',
  lineColor: '100, 180, 255',
  maxDist: 115,
  mouseRadius: 160,
  speed: 0.65,
  sizeMin: 1,
  sizeMax: 3,
  opacityParticle: 0.45,
  opacityLineMax: 0.3,
};

const Hero = () => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    let raf;

    const resize = () => {
      canvas.width = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
    };
    resize();
    window.addEventListener('resize', resize);

    const mouse = { x: null, y: null };
    const particles = Array.from({ length: CFG.count }, () => ({
      x: Math.random() * canvas.width,
      y: Math.random() * canvas.height,
      vx: (Math.random() - 0.5) * CFG.speed,
      vy: (Math.random() - 0.5) * CFG.speed,
      r: CFG.sizeMin + Math.random() * (CFG.sizeMax - CFG.sizeMin),
    }));

    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      particles.forEach((p, i) => {
        p.x += p.vx;
        p.y += p.vy;
        if (p.x < 0 || p.x > canvas.width)  p.vx *= -1;
        if (p.y < 0 || p.y > canvas.height) p.vy *= -1;

        if (mouse.x !== null) {
          const dx = p.x - mouse.x;
          const dy = p.y - mouse.y;
          const d  = Math.sqrt(dx * dx + dy * dy);
          if (d < CFG.mouseRadius) {
            const f = (CFG.mouseRadius - d) / CFG.mouseRadius;
            p.x += (dx / d) * f * 2.5;
            p.y += (dy / d) * f * 2.5;
          }
          if (d < CFG.maxDist + 50) {
            const a = (1 - d / (CFG.maxDist + 50)) * 0.45;
            ctx.beginPath();
            ctx.moveTo(p.x, p.y);
            ctx.lineTo(mouse.x, mouse.y);
            ctx.strokeStyle = `rgba(${CFG.lineColor}, ${a})`;
            ctx.lineWidth = 1.1;
            ctx.stroke();
          }
        }

        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${CFG.color}, ${CFG.opacityParticle})`;
        ctx.fill();

        for (let j = i + 1; j < particles.length; j++) {
          const dx = p.x - particles[j].x;
          const dy = p.y - particles[j].y;
          const d  = Math.sqrt(dx * dx + dy * dy);
          if (d < CFG.maxDist) {
            const a = CFG.opacityLineMax * (1 - d / CFG.maxDist);
            ctx.beginPath();
            ctx.moveTo(p.x, p.y);
            ctx.lineTo(particles[j].x, particles[j].y);
            ctx.strokeStyle = `rgba(${CFG.lineColor}, ${a})`;
            ctx.lineWidth = 0.7;
            ctx.stroke();
          }
        }
      });
      raf = requestAnimationFrame(animate);
    };
    animate();

    const onMove  = (e) => { const r = canvas.getBoundingClientRect(); mouse.x = e.clientX - r.left; mouse.y = e.clientY - r.top; };
    const onLeave = () => { mouse.x = null; mouse.y = null; };
    canvas.addEventListener('mousemove', onMove);
    canvas.addEventListener('mouseleave', onLeave);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', resize);
      canvas.removeEventListener('mousemove', onMove);
      canvas.removeEventListener('mouseleave', onLeave);
    };
  }, []);

  const irA = (id) => {
    const el = document.getElementById(id);
    if (el) window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY - 70, behavior: 'smooth' });
  };

  return (
    <section
      id="hero"
      style={{
        position: 'relative', minHeight: '100vh',
        display: 'flex', alignItems: 'center',
        overflow: 'hidden',
        background: 'linear-gradient(135deg, #0c1220 0%, #0f1e35 50%, #0c1220 100%)',
        padding: 0,
      }}
    >
      {/* Orbs de gradiente de fondo */}
      <div style={{ position: 'absolute', top: '15%', left: '5%', width: '420px', height: '420px', background: 'radial-gradient(circle, rgba(46,139,192,0.1) 0%, transparent 70%)', borderRadius: '50%', pointerEvents: 'none' }} />
      <div style={{ position: 'absolute', bottom: '10%', right: '8%', width: '350px', height: '350px', background: 'radial-gradient(circle, rgba(232,112,42,0.07) 0%, transparent 70%)', borderRadius: '50%', pointerEvents: 'none' }} />

      <canvas
        ref={canvasRef}
        style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', zIndex: 1, pointerEvents: 'all' }}
      />

      <Container style={{ position: 'relative', zIndex: 2, pointerEvents: 'none', padding: '130px 24px 90px' }}>
        <div style={{ maxWidth: '780px' }}>
          <motion.h1
            initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.1 }}
            style={{ fontSize: 'clamp(2.2rem, 5.5vw, 3.9rem)', fontWeight: 700, lineHeight: 1.15, marginBottom: '1.4rem', letterSpacing: '-0.3px' }}
          >
            Tu negocio,{' '}
            <span style={{ background: 'linear-gradient(135deg, #45a8e0, #2e8bc0)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>
              online en Castellón
            </span>
            {' '}sin complicaciones
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.2 }}
            style={{ fontSize: 'clamp(1rem, 2vw, 1.18rem)', color: '#8fa3bf', lineHeight: 1.75, marginBottom: '2.5rem', maxWidth: '640px', fontWeight: 400 }}
          >
            Diseñamos y desarrollamos páginas web adaptadas a las necesidades reales de tu negocio. Presupuesto transparente, plazos cumplidos y soporte local en todo momento.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.3 }}
            style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', pointerEvents: 'all' }}
          >
            <button className="btn-accent-custom" onClick={() => irA('precios')}>
              Configura tu proyecto
              <i className="fa-solid fa-arrow-right ms-2" style={{ fontSize: '0.85rem' }} />
            </button>
            <button className="btn-outline-custom" onClick={() => irA('servicios')}>
              Ver servicios
            </button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8, delay: 0.65 }}
            style={{ display: 'flex', gap: '2.5rem', marginTop: '3.5rem', flexWrap: 'wrap' }}
          >
            {[
              { icon: 'fa-solid fa-chart-line', num: '+50',    label: 'Proyectos entregados' },
              { icon: 'fa-solid fa-star',        num: '100%',   label: 'Clientes satisfechos' },
              { icon: 'fa-solid fa-bolt',        num: '7 días', label: 'Web estática lista' },
            ].map(({ icon, num, label }) => (
              <div key={label}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                  <i className={icon} style={{ color: '#2e8bc0', fontSize: '0.9rem' }} />
                  <span style={{ fontSize: '1.75rem', fontWeight: 700, color: '#45a8e0', fontFamily: 'var(--font-heading)', lineHeight: 1 }}>{num}</span>
                </div>
                <div style={{ fontSize: '0.8rem', color: '#6a86a8' }}>{label}</div>
              </div>
            ))}
          </motion.div>
        </div>
      </Container>

      {/* Indicador de scroll */}
      <div style={{ position: 'absolute', bottom: '32px', left: '50%', transform: 'translateX(-50%)', zIndex: 2, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px', opacity: 0.45 }}>
        <span style={{ fontSize: '0.68rem', color: '#6a86a8', letterSpacing: '3px', fontFamily: 'var(--font-heading)', fontWeight: 600 }}>SCROLL</span>
        <i className="fa-solid fa-chevron-down" style={{ color: '#2e8bc0', animation: 'scrollBounce 1.8s ease-in-out infinite' }} />
        <style>{`@keyframes scrollBounce { 0%,100%{transform:translateY(0);opacity:.4} 50%{transform:translateY(6px);opacity:1} }`}</style>
      </div>
    </section>
  );
};

export default Hero;
